// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ProjectStatus } from '@/constants/project-status';
import { Project } from '@/project/api/types';
import { ProjectProductsLabel } from '@/project/components/project-products-label';
import { getAssignedAssetCount } from '@/project/utilities/get-assigned-asset-count';
import { getRequestedAssetCount } from '@/project/utilities/get-requested-asset-count';
import { formatNumber } from '@/utilities/number';
import { Tooltip, Typography } from '@mui/material';

function getAssetCountLabel(
  requestedCount: number,
  assignedCount: number,
  status: ProjectStatus
) {
  if (requestedCount === 0 && assignedCount === 0) {
    return 'Not set';
  }

  const remainingCount = requestedCount - assignedCount;
  const showDelta =
    status === 'ACTIVE' ||
    status === 'UPCOMING' ||
    status === 'DATE_RANGE_INCOMPLETE';

  if (showDelta && Math.abs(remainingCount) > 0) {
    const remainingLabel = remainingCount > 0 ? 'needed' : 'extra';

    return `${formatNumber(assignedCount)} / ${formatNumber(requestedCount)} (${formatNumber(Math.abs(remainingCount))} ${remainingLabel})`;
  }

  return `${formatNumber(assignedCount)} / ${formatNumber(requestedCount)}`;
}

interface Props {
  /**
   * A project to display the assignments for.
   */
  project: Pick<Project, 'assignments' | 'isArchived' | 'status'>;
}

/**
 * Displays a label for the number of assets assigned to a project.
 */
export function ProjectAssignmentsLabel(props: Props) {
  const assignedAssetCount = getAssignedAssetCount(props.project);
  const requestedAssetCount = getRequestedAssetCount(props.project);
  const assetCountLabel = getAssetCountLabel(
    requestedAssetCount,
    assignedAssetCount,
    props.project.status
  );

  const assetCountLabelColor = props.project.isArchived
    ? 'text.disabled'
    : requestedAssetCount === 0 && assignedAssetCount === 0
      ? 'text.secondary'
      : 'text.primary';

  const assetCountTooltipTitle =
    props.project.assignments.length === 0 ? null : (
      <ProjectProductsLabel
        variant="caption"
        status={props.project.status}
        assignments={props.project.assignments}
      />
    );

  return (
    <Tooltip arrow placement="right" title={assetCountTooltipTitle}>
      <Typography display="inline" variant="body2" color={assetCountLabelColor}>
        {assetCountLabel}
      </Typography>
    </Tooltip>
  );
}
